import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Button,
  Box,
  Grid,
  IconButton,
  Link,
  TextField,
  Tabs,
  Tab,
  Snackbar,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import FaxIcon from '@mui/icons-material/Fax';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SendIcon from '@mui/icons-material/Send';
import { useContent } from '../context/ContentContext';
import { useTranslation } from 'react-i18next';

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    borderRadius: 0,
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    overflow: 'hidden',
  },
});

const ContactItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  padding: theme.spacing(2, 0),
  borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
  '&:last-child': {
    borderBottom: 'none',
  },
}));

const ContactIcon = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(2),
  color: '#000',
}));

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.1)' },
    '&:hover fieldset': { borderColor: 'rgba(0, 0, 0, 0.3)' },
    '&.Mui-focused fieldset': { borderColor: '#000' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#000' },
});

function ContactModal({ open, onClose }) {
  const { content } = useContent();
  const { site } = content;
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const isZh = currentLanguage === 'zh';

  const [tab, setTab] = useState(0);
  const [formData, setFormData] = useState({
    lastName: '', firstName: '', phone: '', email: '', company: '', message: '',
  });
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [submitting, setSubmitting] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mkn5m2nvd4.execute-api.us-east-1.amazonaws.com/dev';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.lastName.trim()) newErrors.lastName = isZh ? '请输入姓' : 'Last name is required';
    if (!formData.firstName.trim()) newErrors.firstName = isZh ? '请输入名' : 'First name is required';
    if (!formData.email.trim()) {
      newErrors.email = isZh ? '请输入邮箱' : 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = isZh ? '请输入有效的邮箱地址' : 'Please enter a valid email';
    }
    if (!formData.message.trim()) newErrors.message = isZh ? '请输入留言' : 'Message is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSnackbar({
          open: true,
          message: isZh ? '消息已发送成功！我们会尽快与您联系。' : 'Message sent successfully! We will contact you soon.',
          severity: 'success'
        });
        // Reset form
        setFormData({
          lastName: '', firstName: '', phone: '', email: '', company: '', message: '',
        });
        // Close modal after 2 seconds
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        throw new Error(data.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setSnackbar({
        open: true,
        message: isZh ? '发送失败，请稍后重试或直接拨打电话联系我们。' : 'Failed to send message. Please try again or contact us by phone.',
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ backgroundColor: '#000', color: '#fff', p: 3, position: 'relative' }}>
        <Typography variant="h5" sx={{ fontWeight: 500 }}>
          {t('common.contactInfo')}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 12, color: '#fff', '&:hover': { opacity: 0.6 } }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        sx={{
          borderBottom: '1px solid rgba(0,0,0,0.1)',
          '& .MuiTab-root': { fontWeight: 500, fontSize: '14px' },
          '& .Mui-selected': { color: '#000' },
          '& .MuiTabs-indicator': { backgroundColor: '#000' },
        }}
      >
        <Tab label={isZh ? '联系方式' : 'Contact Info'} />
        <Tab label={isZh ? '发送消息' : 'Send Message'} />
      </Tabs>

      <DialogContent sx={{ p: 4 }}>
        {tab === 0 ? (
          <Box>

            {site.cell && (
              <ContactItem>
                <ContactIcon><SmartphoneIcon sx={{ fontSize: 20 }} /></ContactIcon>
                <Box>
                  <Typography variant="overline" sx={{ color: '#666', letterSpacing: '0.1em', display: 'block', mb: 0.5 }}>
                    {t('common.cell')}
                  </Typography>
                  <Link href={`tel:${site.cell}`} sx={{ color: '#000', textDecoration: 'none', fontSize: '16px', fontWeight: 500, '&:hover': { opacity: 0.6 } }}>
                    {site.cell}
                  </Link>
                </Box>
              </ContactItem>
            )}

            {site.fax && (
              <ContactItem>
                <ContactIcon><FaxIcon sx={{ fontSize: 20 }} /></ContactIcon>
                <Box>
                  <Typography variant="overline" sx={{ color: '#666', letterSpacing: '0.1em', display: 'block', mb: 0.5 }}>
                    {t('common.fax')}
                  </Typography>
                  <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>{site.fax}</Typography>
                </Box>
              </ContactItem>
            )}

            <ContactItem>
              <ContactIcon><EmailIcon sx={{ fontSize: 20 }} /></ContactIcon>
              <Box>
                <Typography variant="overline" sx={{ color: '#666', letterSpacing: '0.1em', display: 'block', mb: 0.5 }}>
                  {t('common.email')}
                </Typography>
                <Link href={`mailto:${site.email}`} sx={{ color: '#000', textDecoration: 'none', fontSize: '16px', fontWeight: 500, '&:hover': { opacity: 0.6 } }}>
                  {site.email}
                </Link>
              </Box>
            </ContactItem>

            <ContactItem>
              <ContactIcon><LocationOnIcon sx={{ fontSize: 20 }} /></ContactIcon>
              <Box>
                <Typography variant="overline" sx={{ color: '#666', letterSpacing: '0.1em', display: 'block', mb: 0.5 }}>
                  {t('common.address')}
                </Typography>
                <Typography sx={{ fontSize: '16px', fontWeight: 500, lineHeight: 1.5 }}>
                  {isZh ? site.address_zh : site.address}
                </Typography>
              </Box>
            </ContactItem>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <StyledTextField
                  fullWidth size="small"
                  label={isZh ? '姓 *' : 'Last Name *'}
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                />
              </Grid>
              <Grid item xs={6}>
                <StyledTextField
                  fullWidth size="small"
                  label={isZh ? '名 *' : 'First Name *'}
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                />
              </Grid>
              <Grid item xs={6}>
                <StyledTextField
                  fullWidth size="small"
                  label={isZh ? '电话' : 'Phone'}
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <StyledTextField
                  fullWidth size="small"
                  label={isZh ? '邮箱 *' : 'Email *'}
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                />
              </Grid>
              <Grid item xs={12}>
                <StyledTextField
                  fullWidth size="small"
                  label={isZh ? '公司' : 'Company'}
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <StyledTextField
                  fullWidth multiline rows={4}
                  label={isZh ? '留言 *' : 'Message *'}
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  error={!!errors.message}
                  helperText={errors.message}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={submitting}
                  endIcon={<SendIcon />}
                  sx={{
                    backgroundColor: '#000',
                    color: '#fff',
                    borderRadius: 0,
                    py: 1.5,
                    fontWeight: 500,
                    '&:hover': { backgroundColor: '#333' },
                    '&:disabled': { backgroundColor: '#999', color: '#fff' }
                  }}
                >
                  {submitting ? (isZh ? '发送中...' : 'Sending...') : (isZh ? '发送消息' : 'Send Message')}
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </StyledDialog>
  );
}

export default ContactModal;
